import yaml from 'js-yaml';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import versionInfoYml from 'versioninfo.yml';

import * as apiGetDatabaseInfo from 'api/version/GetDatabaseInfo';
import * as apiGetVersionInfoAi from 'api/version/GetVersionAi';
import * as apiGetVersionInfoTracenet from 'api/version/GetVersionTracenet';
import * as apiGetVersionInfoV1 from 'api/version/GetVersionV1';
import SystemInfoComponent from 'components/desktop/SystemInfo/SystemInfoComponent';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import {
	mapAiVersionInfoToGenericVersionInfo,
	mapBackendVersionInfoToGenericVersionInfo,
	mapDbVersionInfoToGenericVersionInfo,
	mapTracenetVersionInfoToGenericVersionInfo,
	mapUiVersionInfoToGenericVersionInfo,
} from 'functions/mappers/VersionInfo/mapToVersionInfo';
import { RootState } from 'reducers/rootReducer';
import { GenericComponentVersionInfo } from 'types/VersionInfo/GenericComponentVersionInfo';
import { UIVersionInfoDto, defaultUIVersionInfoDto } from 'types/VersionInfo/UIVersionInfoDto';

const SystemInfoContainer = () => {
	const [componentsVersionInfo, setComponentsVersionInfo] = useState<
		GenericComponentVersionInfo[]
	>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	useEffect(() => {
		if (reduxCultureCode) {
			initialize();
		}
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		setIsLoading(true);
		await getVersionInfo();
		await getDatabaseVersionInfo();
		setIsLoading(false);
	};

	const getVersionInfo = async (): Promise<void> => {
		const backendVersionInfo = await getBackendVersionInfo();
		const aiVersionInfo = await getAiVersionInfo();
		const tracenetVersionInfo = await getTracenetVersionInfo();
		const uiVersionInfo = await getUIVersionInfo();
		const dbVersionInfo = await getDatabaseVersionInfo();

		setComponentsVersionInfo([
			backendVersionInfo,
			aiVersionInfo,
			tracenetVersionInfo,
			uiVersionInfo,
			dbVersionInfo,
		]);
	};

	const getBackendVersionInfo = async (): Promise<GenericComponentVersionInfo> => {
		const response = await apiGetVersionInfoV1.callApi();
		if (!response.hasValue()) {
			return mapBackendVersionInfoToGenericVersionInfo(null);
		}

		const backendVersion = response.get();
		return mapBackendVersionInfoToGenericVersionInfo(backendVersion);
	};

	const getAiVersionInfo = async (): Promise<GenericComponentVersionInfo> => {
		const response = await apiGetVersionInfoAi.callApi();
		if (!response.hasValue()) {
			return mapAiVersionInfoToGenericVersionInfo(null);
		}

		const aiVersion = response.get();
		return mapAiVersionInfoToGenericVersionInfo(aiVersion);
	};

	const getTracenetVersionInfo = async (): Promise<GenericComponentVersionInfo> => {
		const response = await apiGetVersionInfoTracenet.callApi();
		if (!response.hasValue()) {
			return mapTracenetVersionInfoToGenericVersionInfo(null);
		}

		const tracenetVersion = response.get();
		return mapTracenetVersionInfoToGenericVersionInfo(tracenetVersion);
	};

	const getUIVersionInfo = async (): Promise<GenericComponentVersionInfo> => {
		let uiVersionInfo: UIVersionInfoDto = defaultUIVersionInfoDto();
		try {
			const response = await fetch(versionInfoYml);
			const text = await response.text();
			const data = yaml.load(text);
			uiVersionInfo = data as UIVersionInfoDto;
		} catch (error) {
			console.error('Error loading YAML file:', error);
		}
		return mapUiVersionInfoToGenericVersionInfo(uiVersionInfo);
	};

	const getDatabaseVersionInfo = async (): Promise<GenericComponentVersionInfo> => {
		const response = await apiGetDatabaseInfo.callApi();
		if (!response.hasValue()) {
			return mapDbVersionInfoToGenericVersionInfo(null);
		}

		const databaseVersion = response.get();
		return mapDbVersionInfoToGenericVersionInfo(databaseVersion);
	};

	return (
		<>
			<LoadingAnimation isLoading={isLoading} />
			<SystemInfoComponent componentsVersionInfo={componentsVersionInfo} />
		</>
	);
};

export default SystemInfoContainer;
